import React, { useEffect, useState } from "react";
import {
  useChangeStatusQuery,
  useTeamsQuery,
} from "../../../../Services/settingsAPIs/CommonAPI";
import {
  useDispositionQuery,
  useTicketNameRulesAllQuery,
  useUpdateTicketNameRulesAllMutation,
} from "../../../../Services/ticket/CreateTicket";
import { ReactMultiEmail } from "react-multi-email";
import "react-multi-email/dist/style.css";
import { toast } from "react-toastify";
import { capitalizeFirstLetter } from "../../../reusableComponent/CapitalLetter";

/** Walk disposition tree (any depth) to find a node by id. */
function findDispositionById(
  nodes: { id: number; sub_topics?: any[] }[] | undefined,
  id: number | null
): { id: number; priority?: string; sub_topics?: any[] } | null {
  if (id == null || !nodes?.length) return null;
  for (const node of nodes) {
    if (node.id === id) return node;
    const nested = findDispositionById(node.sub_topics, id);
    if (nested) return nested;
  }
  return null;
}

const WorkflowBasedSLA = () => {
  const { data: teamsData } = useTeamsQuery({});
  const [theTeams, setTheTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState<any>();
  const { data: Disposition } = useDispositionQuery(selectedTeam, {
    skip: !selectedTeam,
  });

  const [selectedIds, setSelectedIds] = useState([]);
  const [options, setOptions] = useState([]);
  const [lastSelectedId, setLastSelectedId] = useState(null);
  const [skipDispositionQuery, setSkipDispositionQuery] =
    useState<boolean>(true);
  const [originalEmails, setOriginalEmails] = useState<Record<string, string[]>>({});
  const [originalRules, setOriginalRules] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [selectedWorkflow, setSelectedWorkflow] = useState("");

  const [priority, setPriority] = useState<any>("");

  const [ticketRules, setTicketRules] = useState<any>(null);
  const [emails, setEmails] = useState<Record<string, string[]>>({});
  const [updatedRules, setUpdatedRules] = useState<any>([]);

  const { data: WorkflowByLastDispositionId } = useChangeStatusQuery(
    lastSelectedId,
    {
      skip: skipDispositionQuery,
    }
  );

  const {
    data: ticketNameRules,
    refetch,
    isFetching: isTicketNameRulesFetching,
  } = useTicketNameRulesAllQuery(
    { selectedTeam, lastSelectedId, selectedWorkflow },
    {
      skip: !selectedTeam || !lastSelectedId || !selectedWorkflow,
    }
  );
  const [updateTicketNameRules] = useUpdateTicketNameRulesAllMutation();

  useEffect(() => {
    if (teamsData) {
      setTheTeams(teamsData);
    }
  }, [teamsData]);

  useEffect(() => {
    if (Disposition) {
      setOptions(Disposition);
    }
  }, [Disposition]);

  /** Keep local ticket rules in sync with RTK result for the active team / disposition / workflow only. */
  useEffect(() => {
    if (!selectedTeam || !lastSelectedId || !selectedWorkflow) {
      return;
    }
    if (isTicketNameRulesFetching && ticketNameRules === undefined) {
      return;
    }
    if (ticketNameRules !== undefined && ticketNameRules !== null) {
      setTicketRules(ticketNameRules);
    }
  }, [
    ticketNameRules,
    selectedTeam,
    lastSelectedId,
    selectedWorkflow,
    isTicketNameRulesFetching,
  ]);

  /** Clears workflow, SLA table, and emails so changing team or any disposition level restarts the flow. */
  const resetDownstreamWorkflowAndSla = () => {
    setSelectedWorkflow("");
    setTicketRules(null);
    setUpdatedRules([]);
    setEmails({});
    setOriginalEmails({});
    setOriginalRules([]);
    setPriority("");
  };

  const handleFirstDropdownChange = (event: any) => {
    setSelectedTeam(Number(event.target.value));
    setSelectedIds([]);
    setLastSelectedId(null);
    setSkipDispositionQuery(true);
    setOptions([]);
    resetDownstreamWorkflowAndSla();
  };

  const handleWorkflowChange = (event) => {
    const next = String(event.target.value ?? "");
    setSelectedWorkflow(next);
    setTicketRules(null);
    setUpdatedRules([]);
    setEmails({});
    setOriginalEmails({});
    setOriginalRules([]);
  };

  /** Single workflow from API: select it so ticket rules query and form stay aligned. */
  useEffect(() => {
    if (skipDispositionQuery || lastSelectedId == null) return;
    const wf = WorkflowByLastDispositionId;
    if (!wf?.id) return;
    const wid = String(wf.id);
    if (selectedWorkflow !== wid) {
      setSelectedWorkflow(wid);
    }
  }, [
    skipDispositionQuery,
    lastSelectedId,
    WorkflowByLastDispositionId,
    selectedWorkflow,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const level = parseInt(name.replace("Level", ""), 10);

    resetDownstreamWorkflowAndSla();

    const parsedId = parseInt(value, 10);
    if (Number.isNaN(parsedId)) {
      return;
    }

    const newSelectedIds = selectedIds.slice(0, level);
    newSelectedIds[level] = parsedId;
    setSelectedIds(newSelectedIds);

    setLastSelectedId(parsedId);

    // Determine options for the next level
    let currentOptions = Disposition;
    for (let i = 0; i < level; i++) {
      const selectedId = newSelectedIds[i];
      if (selectedId !== undefined) {
        const found = currentOptions.find((item) => item.id === selectedId);
        if (found) {
          currentOptions = found.sub_topics;
        } else {
          currentOptions = [];
          break;
        }
      }
    }

    setOptions(currentOptions);

    // Handle new disposition selection
    const dispositionItem = currentOptions.find((item) => item.id === parsedId);
    if (dispositionItem) {
      setPriority(dispositionItem.priority || "");
      if (dispositionItem.sub_topics.length > 0) {
        setOptions(dispositionItem.sub_topics);
        setSkipDispositionQuery(true);
      } else {
        setSkipDispositionQuery(false);
      }
    }
  };

  const renderDropdowns = (data, level) => {
    if (!data || data.length === 0) return null;

    return (
      <>
        <div className="w-100 mw-150px">
          <label className="required">
            {level === 0 ? "Select Disposition" : `Sub Disposition ${level}`}
          </label>
          <select
            name={`Level${level}`}
            className="form-select select2-hidden-accessible"
            onChange={handleChange}
            value={selectedIds[level] || ""}
          >
            <option value="" disabled>
              Select
            </option>
            {data.map((option) => (
              <option key={option.id} value={option.id}>
                {option.topic}
              </option>
            ))}
          </select>
        </div>
        {selectedIds[level] &&
          renderDropdowns(
            data.find((item) => item.id === selectedIds[level])?.sub_topics ||
              [],
            level + 1
          )}
      </>
    );
  };

  useEffect(() => {
    if (ticketRules == null) {
      return;
    }
    if (!ticketRules?.data?.length) {
      setUpdatedRules([]);
      setEmails({});
      setOriginalEmails({});
      setOriginalRules([]);
      return;
    }

    const priorityDuration = ticketRules?.priority_duration || 0;

    const updated: any = {
      ...ticketRules,
      priority_hours: Math.floor(priorityDuration / 3600000),
      priority_minutes: Math.floor((priorityDuration % 3600000) / 60000),

      data: ticketRules?.data?.map((item) => {
        const duration = item?.duration_in_ms || 0;
        return {
          ...item,
          hours: Math.floor(duration / 3600000),
          minutes: Math.floor((duration % 3600000) / 60000),
        };
      }),
    };

    setUpdatedRules(updated);
    setOriginalRules(updated);

    const emailData = ticketRules?.data?.reduce((acc, rule) => {
      acc[rule.id] = rule?.ticket_name_sla_mail || [];
      return acc;
    }, {} as Record<string, string[]>);
    setEmails(emailData);
    setOriginalEmails(emailData);
  }, [ticketRules]);

  const handleTimeChange = (index, type, action) => {
    setUpdatedRules((prevRules) => {
      if (!prevRules || !Array.isArray(prevRules?.data)) return prevRules;

      // Get the maximum allowed duration from priority_duration
      const maxAllowedDuration = prevRules?.priority_duration || 0;
      const maxAllowedHours = Math.floor(maxAllowedDuration / 3600000);
      const maxAllowedMinutes = Math.floor((maxAllowedDuration % 3600000) / 60000);

      const updatedData = prevRules?.data?.map((rule, i) => {
        if (i === index) {
          let newDuration = rule?.duration_in_ms || 0;
          let currentHours = Math.floor(newDuration / 3600000);
          let currentMinutes = Math.floor((newDuration % 3600000) / 60000);

          const otherRulesTotalDuration = prevRules.data
            .filter((_, idx) => idx !== index)
            .reduce((acc, otherRule) => acc + (otherRule?.duration_in_ms || 0), 0);

          const remainingDuration = maxAllowedDuration - otherRulesTotalDuration;
          const maxHoursForCurrentRule = Math.floor(remainingDuration / 3600000);
          const maxMinutesForCurrentRule = Math.floor((remainingDuration % 3600000) / 60000);

          if (type === "hours") {
            if (action === "increment") {
              if (currentHours < maxHoursForCurrentRule) {
                newDuration += 3600000;
              } else {
                toast.warning(`Cannot exceed maximum allowed time. Maximum allowed: ${maxAllowedHours}h ${maxAllowedMinutes}m`);
                return rule;
              }
            } else if (action === "decrement") {
              if (currentHours > 0) {
                newDuration -= 3600000;
              } else {
                toast.warning("Hours cannot be less than 0");
                return rule;
              }
            }
          } else if (type === "minutes") {
            if (action === "increment") {
              const newTotalMinutes = currentHours * 60 + currentMinutes + 1;
              const maxTotalMinutesForCurrentRule =
                maxHoursForCurrentRule * 60 + maxMinutesForCurrentRule;

              if (newTotalMinutes <= maxTotalMinutesForCurrentRule) {
                newDuration += 60000;
              } else {
                toast.warning(`Cannot exceed maximum allowed time. Maximum allowed: ${maxAllowedHours}h ${maxAllowedMinutes}m`);
                return rule;
              }
            } else if (action === "decrement") {
              if (currentMinutes > 0 || currentHours > 0) {
                newDuration = Math.max(0, newDuration - 60000);
              } else {
                toast.warning("Time cannot be less than 0");
                return rule;
              }
            }
          }

          return {
            ...rule,
            duration_in_ms: newDuration,
            hours: Math.floor(newDuration / 3600000),
            minutes: Math.floor((newDuration % 3600000) / 60000),
            isUpdated: true,
          };
        }
        return rule;
      });

      return {
        ...prevRules,
        data: updatedData,
      };
    });
  };

  const handleEmailChange = (ticketId, updatedEmails) => {
    setEmails((prev) => ({
      ...prev,
      [ticketId]: updatedEmails,
    }));

    setUpdatedRules((prevRules) => {
      if (!prevRules?.data) return prevRules;
      const updatedData = prevRules.data.map((item) =>
        item.id === ticketId
          ? {
              ...item,
              ticket_name_sla_mail: updatedEmails,
              isUpdated: true,
            }
          : item
      );
      return { ...prevRules, data: updatedData };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!selectedTeam) {
      toast.error("Please select a team first.");
      return;
    }
    if (lastSelectedId == null) {
      toast.error("Please complete disposition selection.");
      return;
    }
    const leaf = findDispositionById(Disposition, lastSelectedId);
    if (!leaf || (leaf.sub_topics?.length ?? 0) > 0) {
      toast.error("Select a final disposition (no further sub-dispositions).");
      return;
    }
    if (!selectedWorkflow) {
      toast.error("Please select a workflow.");
      return;
    }

    const workflowId = Number(selectedWorkflow);
    if (Number.isNaN(workflowId)) {
      toast.error("Invalid workflow.");
      return;
    }

    setIsLoading(true);

    const rulesList = updatedRules?.data;
    if (!Array.isArray(rulesList) || !rulesList.length) {
      toast.error("No SLA rules to save.");
      setIsLoading(false);
      return;
    }

    const maxAllowedSlaDuration = updatedRules?.priority_duration || 0;

    const totalAllRulesDurationMs = rulesList.reduce((acc, rule) => {
      const ms =
        typeof rule?.duration_in_ms === "number"
          ? rule.duration_in_ms
          : (Number(rule?.hours) * 3600 + Number(rule?.minutes) * 60) * 1000;
      return acc + (Number.isFinite(ms) ? ms : 0);
    }, 0);

    if (
      maxAllowedSlaDuration > 0 &&
      totalAllRulesDurationMs > maxAllowedSlaDuration
    ) {
      toast.error(
        "Sum of all status durations must not exceed the overall SLA duration."
      );
      setIsLoading(false);
      return;
    }

    const payloads = rulesList
      .filter((rule) => rule?.isUpdated)
      .map((rule) => {
        const durationMs =
          typeof rule?.duration_in_ms === "number"
            ? rule.duration_in_ms
            : (Number(rule?.hours) * 3600 + Number(rule?.minutes) * 60) * 1000;
        return {
          id: rule.id,
          status: rule.status,
          duration_in_ms: durationMs,
          ticket_name_sla_mail: emails[rule.id] ?? rule.ticket_name_sla_mail ?? [],
          ticket_name: workflowId,
          action: "",
          disposition: lastSelectedId,
          team: selectedTeam,
        };
      });

    if (!payloads.length) {
      toast.error("No changes to save. Adjust time or notification emails.");
      setIsLoading(false);
      return;
    }

    try {
      await updateTicketNameRules({
        body: payloads,
        selectedTeam,
        lastSelectedId,
        selectedWorkflow,
      }).unwrap();

      toast.success("SL rules updated successfully!");
      await refetch();
      setIsLoading(false);
    } catch (error) {
      toast.error("Failed to update SL rules.");
    }

    setIsLoading(false);
  };

  const isChanged =
    JSON.stringify(emails) !== JSON.stringify(originalEmails) ||
    JSON.stringify(updatedRules) !== JSON.stringify(originalRules);

  return (
    <div className="row g-5 g-xl-8 mb-6">
      <div className="d-flex gap-3 align-items-start flex-wrap">
        <div className="w-100 mw-150px">
          <label className="required">Select Team</label>
          <select className="form-select" onChange={handleFirstDropdownChange}>
            <option value="" disabled selected>
              Select team
            </option>
            {theTeams.map((team, index) => (
              <option key={index} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>

        {selectedTeam && renderDropdowns(Disposition, 0)}

        {lastSelectedId && !skipDispositionQuery && (
          <>
            {WorkflowByLastDispositionId && (
              <div className="w-100 mw-150px">
                <label className="required">Select Workflow</label>
                <select
                  className="form-select"
                  value={selectedWorkflow}
                  onChange={handleWorkflowChange}
                >
                  <option value="" disabled>
                    Select Workflow
                  </option>
                  <option
                    key={WorkflowByLastDispositionId.id}
                    value={WorkflowByLastDispositionId.id}
                  >
                    {WorkflowByLastDispositionId.name}
                  </option>
                </select>
              </div>
            )}

            {selectedWorkflow && (
              <>
                <div className="w-100 mw-150px">
                  <label className="required">Priority</label>
                  <select
                    className="form-select"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="" disabled>
                      Select Priority
                    </option>
                    <option value={priority}>{priority}</option>
                  </select>
                </div>

                <div className="d-flex flex-row gap-3">
                  <div className="w-100 mw-150px">
                    <label>Hours</label>
                    <input
                      type="text"
                      className="form-control"
                      value={updatedRules.priority_hours}
                      readOnly
                    />
                  </div>
                  <div className="w-100 mw-150px">
                    <label>Minutes</label>
                    <input
                      type="text"
                      className="form-control"
                      value={updatedRules.priority_minutes}
                      readOnly
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
      {selectedWorkflow && (
        <>
          <div className="mt-5 mx-n3">
            <div className="">
              <div
                className="card my-2 min-h-40px justify-content-center"
                style={{ backgroundColor: "rgba(228, 228, 228, 0.498)" }}
              >
                <div className="row align-items-center">
                  <div className="col-2 text-start ps-7">
                    <strong>Status</strong>
                  </div>

                  <div className="col-2 text-center">
                    <strong>Hours</strong>
                  </div>
                  <div className="col-2 text-center">
                    <strong>Minutes</strong>
                  </div>
                  <div className="col-6 text-center">
                    <strong>Mail ID</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {updatedRules?.data?.length > 0 &&
            updatedRules?.data?.map((rule, index) => (
              <div className="row" key={index}>
                <div className="input-group-text mt-2 bg-white">
                  <div className="row w-100 align-items-center">
                    <div className="col-2 text-start ps-5">
                      {capitalizeFirstLetter(rule.status)}
                    </div>

                    <div className="col-2 d-flex align-items-center justify-content-center">
                      {rule.hours}
                      <div className="d-flex flex-column ms-2">
                        <i
                          className="bi bi-caret-up-fill cursor-pointer"
                          onClick={() =>
                            handleTimeChange(index, "hours", "increment")
                          }
                        ></i>
                        <i
                          className="bi bi-caret-down-fill cursor-pointer"
                          onClick={() =>
                            handleTimeChange(index, "hours", "decrement")
                          }
                        ></i>
                      </div>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-center">
                      {rule.minutes}
                      <div className="d-flex flex-column ms-2">
                        <i
                          className="bi bi-caret-up-fill cursor-pointer"
                          onClick={() =>
                            handleTimeChange(index, "minutes", "increment")
                          }
                        ></i>
                        <i
                          className="bi bi-caret-down-fill cursor-pointer"
                          onClick={() =>
                            handleTimeChange(index, "minutes", "decrement")
                          }
                        ></i>
                      </div>
                    </div>
                    <div className="col-6 text-center">
                      <ReactMultiEmail
                        placeholder="Enter Email"
                        emails={emails[rule.id] || []}
                        onChange={(updatedEmails) =>
                          handleEmailChange(rule.id, updatedEmails)
                        }
                        getLabel={(email, index, removeEmail) => (
                          <div data-tag key={index}>
                            {email}
                            <span
                              data-tag-handle
                              onClick={() => removeEmail(index)}
                            >
                              ×
                            </span>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          <div className="row">
            <div className="col text-end mt-2">
              <button
                className="btn btn-primary ms-4 min-w-100px"
                onClick={handleSave}
                disabled={isLoading || !isChanged}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WorkflowBasedSLA;
